import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useMessages } from '../context/MessagesContext';
import './DashboardPages.css';

const SUBJECT_LABELS = {
  transport: 'Transport Request',
  partnership: 'Partnership Inquiry',
  support: 'General Support',
  other: 'Other',
};

const POLL_MS = 15000;

function formatDateTime(createdAt) {
  if (!createdAt) return '—';
  return new Date(createdAt).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MessagesPage() {
  const { unreadCount, refreshUnreadCount } = useMessages();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadMessages = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const { messages: data } = await api.getMessages();
      setMessages(data || []);
      setLastUpdated(new Date());
      await refreshUnreadCount();
    } catch (err) {
      setError(err.message);
      if (!silent) setMessages([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [refreshUnreadCount]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const id = setInterval(() => loadMessages({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [loadMessages]);

  const selected = messages.find((m) => m.message_id === selectedId) || null;

  const openMessage = async (msg) => {
    setSelectedId(msg.message_id);
    if (!msg.is_read) {
      try {
        await api.markMessageRead(msg.message_id);
        setMessages((prev) =>
          prev.map((m) =>
            m.message_id === msg.message_id ? { ...m, is_read: true } : m
          )
        );
        await refreshUnreadCount();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try {
      setDeleting(true);
      setError(null);
      await api.deleteMessage(id);
      if (selectedId === id) setSelectedId(null);
      await loadMessages({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dashboard-page messages-page">
      <h1>Messages</h1>
      <p className="dashboard-subtitle">
        Contact form inbox — when someone submits the public Contact page, their message appears here
        for administrators.
      </p>

      <section className="entity-manual-panel messages-inbox-panel" aria-labelledby="inbox-heading">
        <h2 id="inbox-heading" className="entity-manual-title">
          Admin contact inbox
        </h2>
        <p className="orders-hint entity-manual-hint">
          Unread: <strong>{unreadCount}</strong>
          {lastUpdated && (
            <>
              {' '}
              · Last checked {formatDateTime(lastUpdated)}
            </>
          )}
        </p>
        <div className="orders-toolbar messages-toolbar">
          <button
            type="button"
            className="orders-refresh-btn"
            onClick={() => loadMessages({ silent: true })}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh inbox'}
          </button>
          <span className="orders-hint">
            New submissions also show in Activity Log and the bell icon in the header.
          </span>
        </div>
      </section>

      {error && <div className="orders-error">{error}</div>}

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading messages…</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="orders-placeholder">
          <p>No contact messages yet.</p>
          <p className="orders-hint">
            Test: open the website Contact page, fill Name, Email, Subject, and Message, then click
            Send Message — it should appear here within a few seconds (use Refresh if needed).
          </p>
        </div>
      ) : (
        <div className="messages-inbox">
          <ul className="messages-list">
            {messages.map((m) => (
              <li key={m.message_id}>
                <button
                  type="button"
                  className={[
                    'messages-list-item',
                    !m.is_read ? 'messages-list-item-unread' : '',
                    selectedId === m.message_id ? 'messages-list-item-active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => openMessage(m)}
                >
                  <span className="messages-list-top">
                    <strong>{m.name}</strong>
                    {!m.is_read && <span className="messages-unread-pill">New</span>}
                  </span>
                  <span className="messages-list-subject">
                    {SUBJECT_LABELS[m.subject] || m.subject}
                  </span>
                  <span className="messages-list-preview">
                    {String(m.body || '').slice(0, 80)}
                    {m.body && m.body.length > 80 ? '…' : ''}
                  </span>
                  <span className="messages-list-time">{formatDateTime(m.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="messages-detail">
            {!selected ? (
              <div className="messages-detail-empty">
                <p>Select a message to read full details</p>
              </div>
            ) : (
              <>
                <div className="messages-detail-header">
                  <div>
                    <h2>{selected.name}</h2>
                    <p className="messages-detail-meta">
                      #{selected.message_id} · {formatDateTime(selected.created_at)}
                      {' · '}
                      {SUBJECT_LABELS[selected.subject] || selected.subject}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="entity-btn entity-delete"
                    onClick={() => handleDelete(selected.message_id)}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
                <dl className="messages-detail-fields">
                  <div>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${selected.email}`}>{selected.email}</a>
                    </dd>
                  </div>
                  {selected.phone && (
                    <div>
                      <dt>Phone</dt>
                      <dd>
                        <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="messages-detail-body">
                  <h3>Message</h3>
                  <p>{selected.body}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagesPage;
